import { Redirect, useRouter } from "./router";
import { AppStateProvider } from "./state/appState";
import { useSession } from "./state/session";
import { currentSurface, surfaceHref, type Surface } from "./surface";
import { LoginScreen } from "./screens/Login";
import { KitchenScreen } from "./screens/Kitchen";
import { CourierScreen } from "./screens/Courier";
import { CounterPage } from "./screens/Counter";
import { CounterLayout } from "./screens/CounterLayout";
import { AdminLayout } from "./screens/admin/AdminLayout";
import { DashboardPage } from "./screens/admin/Dashboard";
import { OrdersPage } from "./screens/admin/Orders";
import { MenuPage } from "./screens/admin/Menu";
import { BannersPage } from "./screens/admin/Banners";
import { PromotionsPage } from "./screens/admin/Promotions";
import { PromoCodesPage } from "./screens/admin/PromoCodes";
import { AnalyticsPage } from "./screens/admin/Analytics";
import { StaffPage } from "./screens/admin/Staff";
import { RolesPage } from "./screens/admin/Roles";
import { BenchesPage } from "./screens/admin/Benches";
import { PaymentsPage } from "./screens/admin/Payments";
import { DevicesPage } from "./screens/admin/Devices";
import { FiscalPage } from "./screens/admin/Fiscal";
import { SettingsPage } from "./screens/admin/Settings";
import { StorefrontPage } from "./screens/admin/Storefront";
import { PurchasesPage } from "./screens/admin/Purchases";
import { StockPage } from "./screens/admin/Stock";
import { CustomersPage } from "./screens/admin/Customers";
import { AuditPage } from "./screens/admin/Audit";
import type { Staff } from "./api/types";

/**
 * Куда сотрудник попадает по своей роли.
 *
 * Возвращается адрес целиком: экраны разъехались по поддоменам, и повар,
 * открывший админку, должен уехать на свой хост, а не на свой путь.
 *
 * Владелец идёт в админку, все остальные — на своё рабочее место. Старший
 * смены попадает на кассу: там же он ведёт и заказы, и стоп-лист, и отчёты.
 */
export function homeForRole(role: Staff["role"]): string {
  if (role === "kitchen") return surfaceHref("kitchen");
  if (role === "courier") return surfaceHref("courier");
  if (role === "owner") return surfaceHref("admin");
  return surfaceHref("counter");
}

/**
 * Разделы рабочего места смены.
 *
 * Это те же страницы, что и в админке: разделять их значило бы вести две
 * копии одного экрана. Разный здесь только каркас и набор разделов.
 */
const COUNTER_PAGES: Record<string, () => JSX.Element> = {
  "/": CounterPage,
  "/orders": OrdersPage,
  "/menu": MenuPage,
  "/stock": StockPage,
  "/purchases": PurchasesPage,
  "/payments": PaymentsPage,
  "/analytics": AnalyticsPage,
  "/devices": DevicesPage,
  "/fiscal": FiscalPage
};

/** Разделы админки. Пути короткие: у админки свой поддомен, префикс не нужен. */
const ADMIN_PAGES: Record<string, () => JSX.Element> = {
  "/": DashboardPage,
  "/orders": OrdersPage,
  "/menu": MenuPage,
  "/promotions": PromotionsPage,
  "/banners": BannersPage,
  "/promo-codes": PromoCodesPage,
  "/analytics": AnalyticsPage,
  "/customers": CustomersPage,
  "/stock": StockPage,
  "/purchases": PurchasesPage,
  "/benches": BenchesPage,
  "/payments": PaymentsPage,
  "/devices": DevicesPage,
  "/fiscal": FiscalPage,
  "/staff": StaffPage,
  "/roles": RolesPage,
  "/settings": SettingsPage,
  "/storefront": StorefrontPage,
  "/audit": AuditPage
};

/** Роль, которой этот экран доступен по смыслу, а не по правам. */
const SURFACE_HINT: Record<Exclude<Surface, "unknown">, string> = {
  kitchen: "кухни",
  courier: "доставки",
  counter: "смены",
  admin: "владельца"
};

export function App() {
  const { path } = useRouter();
  const { staff, loading, can } = useSession();
  const surface = currentSurface();

  if (loading) return <div className="page">Загрузка…</div>;

  if (surface === "unknown") {
    return (
      <div className="page">
        <h1>Неизвестный адрес</h1>
        <p>
          Рабочие экраны открываются по своим адресам: kitchen — кухня, courier — доставка, kassa — касса, admin —
          админка.
        </p>
      </div>
    );
  }

  // Вход показывается на месте запрошенного экрана и не меняет адрес: после
  // входа сотрудник остаётся там, куда шёл.
  if (!staff) return <LoginScreen surfaceHint={SURFACE_HINT[surface]} />;

  if (surface === "kitchen") {
    return (
      <AppStateProvider>
        <KitchenScreen />
      </AppStateProvider>
    );
  }

  if (surface === "courier") {
    return (
      <AppStateProvider>
        <CourierScreen />
      </AppStateProvider>
    );
  }

  if (surface === "counter") {
    const CounterSection = COUNTER_PAGES[path];
    if (!CounterSection) return <Redirect to="/" />;

    return (
      <AppStateProvider>
        <CounterLayout>
          {/* Касса разложена на всё полотно сама; прочие разделы — обычные страницы панели. */}
          {path === "/" ? (
            <CounterSection />
          ) : (
            <main className="page">
              <CounterSection />
            </main>
          )}
        </CounterLayout>
      </AppStateProvider>
    );
  }

  /*
   * Админка — рабочее место владельца. Права всё равно проверяются на каждом
   * запросе, но пускать сюда старшего смены незачем: его разделы живут на
   * кассе, и второй адрес с тем же содержимым только путал бы смену.
   */
  if (!can("settings.manage")) {
    return <OwnerOnly home={homeForRole(staff.role)} />;
  }

  const AdminPage = ADMIN_PAGES[path];
  if (AdminPage) {
    return (
      <AppStateProvider>
        <AdminLayout>
          <AdminPage />
        </AdminLayout>
      </AppStateProvider>
    );
  }

  return <Redirect to="/" />;
}

/**
 * Сотрудник открыл админку, а она не его.
 *
 * Пустая панель без единого раздела выглядела бы поломкой, а отказ доступа —
 * подозрением в чужом умысле. Человек просто набрал не тот адрес, и ему нужна
 * дорога к своему экрану.
 */
function OwnerOnly({ home }: { home: string }) {
  const { signOut } = useSession();

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <p className="eyebrow">Админка</p>
          <h1>Этот адрес — для владельца</h1>
        </div>
      </div>

      <p className="muted">
        Меню и цены, акции, лавочки, сотрудники и настройки точки меняются отсюда. Заказы, стоп-лист, склад,
        возвраты и отчёты смены — на вашем рабочем месте.
      </p>

      {/* Выход нужен здесь же: сюда попадают, когда вошли не под тем логином,
          а шапки с кнопкой «Выйти» на этом экране нет. */}
      <p className="row-actions">
        <a className="btn btn-primary" href={home}>
          Перейти к своей смене
        </a>
        <button type="button" className="btn" onClick={() => void signOut()}>
          Выйти
        </button>
      </p>
    </div>
  );
}
